import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Button,
  Card,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { Formik, Form, FieldArray } from "formik";
import * as yup from "yup";
import { v4 as uuidv4 } from "uuid";
import DeleteIcon from "@mui/icons-material/Delete";
import ClearIcon from "@mui/icons-material/Clear";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { getIn } from "formik";

import { removeUndefinedFields } from "../../../../redux/features/utils";
import OrderFormFieldView from "../forms/OrderFormFieldView";
import PriceFormatCustom from "./PriceFormatCustom";

// Utility functions
export const initialValueForOrderFormField = ({ type, value }) => {
  switch (type) {
    case "smallText":
    case "largeText":
      return value;
    case "multiSelectDropdown":
    case "multiSelectRadio":
      return value?.key || "";
    case "checkbox":
      return value || false;
    case "multiSelectCheckbox":
      if (value) {
        return Object.keys(value).map((index) => value[index].key);
      }
      return [];
    default:
      return value;
  }
};

const priceText = (ticketType) => {
  let dollars = 0;
  let USDollar = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  });
  if (ticketType?.usdCents) {
    dollars = ticketType.usdCents / 100;
  }
  return USDollar.format(dollars);
};

export const validationObject = (orderFormField, ticket) => {
  const {
    type,
    name,
    nameForTicket,
    ticketTypeSelection,
    collectForTicketTypes,
  } = orderFormField;

  let nameText = ticket && nameForTicket ? nameForTicket : name;

  let validationObj;

  const isTicket = Boolean(ticket);

  switch (type) {
    case "smallText":
    case "largeText":
    case "multiSelectDropdown":
    case "multiSelectRadio":
      validationObj = yup.string(nameText).trim();
      break;
    case "checkbox":
      validationObj = yup.boolean();
      break;
    case "multiSelectCheckbox":
      validationObj = yup.array();
      break;
    default:
      validationObj = yup.string(nameText).trim();
      break;
  }

  if (orderFormField.key === "email") {
    validationObj = validationObj.email("Enter a valid email");
  }
  if (orderFormField.key === "zipcode") {
    validationObj = yup
      .mixed()
      .test("valid", "ZIP code must be valid", (value) => {
        return /(^\d{5}$)|(^\d{5}-\d{4}$)/.test(value);
      });
  }
  if (orderFormField.required) {
    validationObj = validationObj.when("tickets", (tickets, schema) => {
      if (isTicket) {
        return schema.required(`${nameText} is required`);
      }
      if (ticketTypeSelection === "all") {
        return schema.required(`${nameText} is required`);
      }
      return schema.when("tickets", (tickets, schema) => {
        const hasMatchingTicket = tickets?.some(({ ticketTypeKey }) =>
          collectForTicketTypes.includes(ticketTypeKey),
        );
        return hasMatchingTicket
          ? schema.required(`${nameText} is required`)
          : schema;
      });
    });
  }
  return validationObj;
};

const purchaserOrderFormFieldKeysForTicketTypes = (
  orderFormFields,
  ticketTypeKeys,
) =>
  orderFormFields
    .filter(
      ({ collectFrom, ticketTypeSelection, collectForTicketTypes }) => {
        if (collectFrom !== "both" && collectFrom !== "purchaser") {
          return false;
        }
        return (
          ticketTypeSelection === "all" ||
          (collectForTicketTypes || []).some((key) =>
            ticketTypeKeys.includes(key),
          )
        );
      },
    )
    .map(({ key }) => key);

const attendeeOrderFormFieldKeysForTicketType = (
  orderFormFields,
  ticketTypeKey,
) =>
  orderFormFields
    .filter(
      ({ collectFrom, ticketTypeSelection, collectForTicketTypes }) => {
        if (collectFrom !== "both" && collectFrom !== "attendee") {
          return false;
        }
        return (
          ticketTypeSelection === "all" ||
          (collectForTicketTypes || []).includes(ticketTypeKey)
        );
      },
    )
    .map(({ key }) => key);

export const sanitizeNewOrderValues = (event, values) => {
  const { orderFormFields } = event;
  const sanitizedValues = { ...values };
  orderFormFields.forEach(({ key, type }) => {
    if (type === "multiSelectCheckbox" && sanitizedValues[key]) {
      sanitizedValues[key] = sanitizedValues[key].filter((value) => value);
    }
  });

  const ticketTypeKeys = (values.tickets || []).map((t) => t.ticketTypeKey);
  const allowedPurchaserKeys = new Set(
    purchaserOrderFormFieldKeysForTicketTypes(orderFormFields, ticketTypeKeys),
  );
  const purchaserFieldKeys = orderFormFields
    .filter(
      (f) => f.collectFrom === "both" || f.collectFrom === "purchaser",
    )
    .map((f) => f.key);
  purchaserFieldKeys.forEach((key) => {
    if (!allowedPurchaserKeys.has(key)) {
      delete sanitizedValues[key];
    }
  });

  if (sanitizedValues.tickets?.length) {
    sanitizedValues.tickets = sanitizedValues.tickets.map((ticket) => {
      const { ticketTypeKey } = ticket;
      const allowedKeys = new Set([
        "key",
        "ticketTypeKey",
        "price",
        ...attendeeOrderFormFieldKeysForTicketType(
          orderFormFields,
          ticketTypeKey,
        ),
      ]);
      return Object.fromEntries(
        Object.entries(ticket).filter(([k]) => allowedKeys.has(k)),
      );
    });
  }

  return sanitizedValues;
};

// Exported functions for testing
export const createInitialValues = (orderFormFields) => {
  return orderFormFields
    .filter(({ collectFrom, ticketTypeSelection, collectForTicketTypes }) => {
      if (collectFrom !== "both" && collectFrom !== "purchaser") {
        return false;
      }
      return true;
    })
    .reduce(
      (acc, orderFormField) => {
        return {
          ...acc,
          [orderFormField.key]: initialValueForOrderFormField(orderFormField),
        };
      },
      { ticketType: "", tickets: [] },
    );
};

export const createTicketValidationSchemas = (ticketTypes, orderFormFields) => {
  return ticketTypes.reduce((acc, ticketType) => {
    const { key } = ticketType;
    return {
      ...acc,
      [key]: orderFormFields
        .filter(
          ({ collectFrom, ticketTypeSelection, collectForTicketTypes }) => {
            if (collectFrom !== "both" && collectFrom !== "attendee") {
              return false;
            }
            return (
              ticketTypeSelection === "all" ||
              collectForTicketTypes.includes(key)
            );
          },
        )
        .reduce((acc, orderFormField) => {
          return {
            ...acc,
            [orderFormField.key]: validationObject(orderFormField, true),
          };
        }, {}),
    };
  }, {});
};

export const createTicketsValidationSchema = (ticketValidationSchemas) => {
  return yup.array().of(
    yup.lazy((value) => {
      if (!value) {
        return yup.object().shape({});
      }

      const { ticketTypeKey } = value;

      if (
        !ticketTypeKey ||
        !ticketValidationSchemas ||
        !ticketValidationSchemas[ticketTypeKey]
      ) {
        return yup.object().shape({});
      }

      return yup.object().shape(ticketValidationSchemas[ticketTypeKey]);
    }),
  );
};

export const createValidationSchema = (
  orderFormFields,
  ticketsValidationSchema,
) => {
  return orderFormFields
    .filter(({ collectFrom }) => {
      if (collectFrom !== "both" && collectFrom !== "purchaser") {
        return false;
      }
      return true;
    })
    .reduce(
      (acc, orderFormField) => {
        return {
          ...acc,
          [orderFormField.key]: validationObject(orderFormField),
        };
      },
      { tickets: ticketsValidationSchema },
    );
};

const NewTicketModal = ({ open, event, handleClose, handleSave }) => {
  const [selectedTicketType, setSelectedTicketType] = useState("");
  const { ticketTypes, orderFormFields } = event;
  const [sendConfirmationEmail, setSendConfirmationEmail] = useState(false);
  const [sendAttendees, setSendAttendees] = useState(false);

  useEffect(() => {
    setSendConfirmationEmail(false);
    setSendAttendees(false);
  }, [open]);

  const initialValues = useMemo(() => {
    return createInitialValues(orderFormFields);
  }, [orderFormFields]);

  const ticketValidationSchemas = useMemo(() => {
    return createTicketValidationSchemas(ticketTypes, orderFormFields);
  }, [ticketTypes, orderFormFields]);

  const ticketsValidationSchema = useMemo(() => {
    return createTicketsValidationSchema(ticketValidationSchemas);
  }, [ticketValidationSchemas]);

  const validationSchema = useMemo(() => {
    return createValidationSchema(orderFormFields, ticketsValidationSchema);
  }, [orderFormFields, ticketsValidationSchema]);

  return (
    <Modal open={open}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "100%", sm: "100%", md: 600 },
          bgcolor: "background.paper",
          border: "2px solid #000",
          boxShadow: 24,
          p: 4,
          maxHeight: "90vh",
          overflow: "auto",
        }}
      >
        <Formik
          initialValues={initialValues}
          validationSchema={yup.object(validationSchema)}
          onSubmit={async (values, submitProps) => {
            if (handleSave) {
              await handleSave({
                eventId: event.id,
                sendConfirmationEmail,
                sendAttendees,
                values: removeUndefinedFields(
                  sanitizeNewOrderValues(event, values),
                ),
              });
            }
            await handleClose();
          }}
        >
          {({
            values,
            touched,
            errors,
            handleChange,
            handleBlur,
            isValid,
            dirty,
            setValues,
            setFieldValue,
            handleReset,
            submitForm,
            setFieldTouched,
          }) => {
            const formikProps = {
              values,
              touched,
              errors,
              handleChange,
              handleBlur,
              setFieldValue,
            };

            const purchaserOrderFormFields = orderFormFields.filter(
              ({ collectFrom, ticketTypeSelection, collectForTicketTypes }) => {
                if (collectFrom !== "both" && collectFrom !== "purchaser") {
                  return false;
                }
                return (
                  ticketTypeSelection === "all" ||
                  collectForTicketTypes.find((key) =>
                    (values.tickets || []).find(
                      ({ ticketTypeKey }) => key === ticketTypeKey,
                    ),
                  )
                );
              },
            );

            return (
              <Form noValidate autoComplete="off">
                <Box display={"flex"} flexDirection={"column"}>
                  <Typography variant="h5">Contact Information</Typography>
                  <Box>
                    {purchaserOrderFormFields
                      .filter(({ contactInfo }) => contactInfo)
                      .map((orderFormField, i) => {
                        return (
                          <Box key={`contact-field-${i}`}>
                            <OrderFormFieldView
                              {...{
                                formikProps,
                                orderFormField,
                                orderFormFieldKey: orderFormField.key,
                              }}
                            />
                          </Box>
                        );
                      })}
                  </Box>
                  <FieldArray name={"tickets"}>
                    {({ push, remove }) => {
                      return (
                        <Box
                          display={"flex"}
                          gap={"20px"}
                          flexDirection={"column"}
                          sx={{ marginTop: "20px" }}
                        >
                          {values.tickets.map((ticket, i) => {
                            const { ticketTypeKey, key: ticketKey } = ticket;
                            const ticketType = ticketTypes.find(
                              ({ key }) => key === ticketTypeKey,
                            );

                            const priceKey = `tickets[${i}][price]`;
                            let priceValue = (
                              (getIn(values, priceKey) || 0) / 100
                            ).toFixed(2);
                            return (
                              <Card
                                key={`ticket-${ticketTypeKey}-${i}`}
                                sx={{ padding: "20px" }}
                                elevation={5}
                              >
                                <Grid
                                  container
                                  justifyContent="flex-end"
                                  sx={{ padding: "15px" }}
                                >
                                  <Grid item xs={9}>
                                    <Box>
                                      <Typography
                                        variant="h6"
                                        component={"span"}
                                      >
                                        {ticketType?.name}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                  <Grid item xs={3}>
                                    <Box
                                      display={"flex"}
                                      flexDirection={"row"}
                                      alignItems={"center"}
                                    >
                                      <Typography
                                        variant="h6"
                                        component={"span"}
                                        sx={{ textAlign: "right" }}
                                        onClick={() => {
                                          setFieldValue(
                                            priceKey,
                                            ticketType?.usdCents || 0,
                                          );
                                        }}
                                      >
                                        {priceText(ticketType)}
                                      </Typography>
                                      <IconButton
                                        data-testid="delete-ticket-button"
                                        sx={{ marginLeft: "auto" }}
                                        onClick={(e) => {
                                          remove(i);
                                        }}
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    </Box>
                                  </Grid>
                                  <Grid item xs={12}>
                                    <Box
                                      xs={12}
                                      sx={{
                                        marginTop: "20px",
                                      }}
                                    >
                                      <Typography sx={{ marginBottom: "5px" }}>
                                        Price Paid
                                      </Typography>
                                      <TextField
                                        data-testid={`price-input-${priceKey}`}
                                        id={priceKey}
                                        name={priceKey}
                                        sx={{ width: "150px" }}
                                        variant="outlined"
                                        value={priceValue}
                                        onChange={(event) => {
                                          let value = parseInt(
                                            parseFloat(event.target.value) *
                                              100,
                                          );
                                          value = Number.isNaN(value)
                                            ? 0
                                            : value;
                                          setFieldValue(priceKey, value);
                                        }}
                                        onBlur={handleBlur}
                                        InputProps={{
                                          startAdornment: (
                                            <InputAdornment position="start">
                                              $
                                            </InputAdornment>
                                          ),
                                          endAdornment: (
                                            <IconButton
                                              data-testid="clear-price-button"
                                              onClick={() => {
                                                setFieldValue(priceKey, 0);
                                              }}
                                            >
                                              <ClearIcon />
                                            </IconButton>
                                          ),
                                          inputComponent: PriceFormatCustom,
                                        }}
                                      />
                                    </Box>
                                  </Grid>
                                  <Grid item xs={12} sx={{ marginTop: "30px" }}>
                                    <Button
                                      data-testid="copy-contact-info-button"
                                      startIcon={<ContentCopyIcon />}
                                      onClick={async (e) => {
                                        e.preventDefault();
                                        orderFormFields
                                          .filter(
                                            ({ contactInfo }) => contactInfo,
                                          )
                                          .forEach(({ key }) => {
                                            setFieldValue(
                                              `tickets[${i}][${key}]]`,
                                              values[key],
                                            );
                                          });
                                      }}
                                    >
                                      Use Contact Information
                                    </Button>
                                  </Grid>
                                  <Grid item xs={12}>
                                    {orderFormFields
                                      .filter(
                                        ({
                                          collectFrom,
                                          ticketTypeSelection,
                                          collectForTicketTypes,
                                        }) => {
                                          if (
                                            collectFrom !== "both" &&
                                            collectFrom !== "attendee"
                                          ) {
                                            return false;
                                          }
                                          return (
                                            ticketTypeSelection === "all" ||
                                            collectForTicketTypes.includes(
                                              ticketTypeKey,
                                            )
                                          );
                                        },
                                      )
                                      .map((orderFormField) => {
                                        return (
                                          <OrderFormFieldView
                                            key={`ticketOrderFormFieldView-${ticketKey}-${orderFormField.key}`}
                                            {...{
                                              formikProps,
                                              orderFormField,
                                              ticket,
                                              orderFormFieldKey: `tickets[${i}][${orderFormField.key}]]`,
                                            }}
                                          />
                                        );
                                      })}
                                  </Grid>
                                </Grid>
                              </Card>
                            );
                          })}
                          <Box display={"flex"} sx={{ marginTop: "20px" }}>
                            <FormControl
                              sx={{ width: "100%", marginTop: "20px" }}
                            >
                              <InputLabel id={"ticketTypeSelectorLabel"}>
                                Ticket Type
                              </InputLabel>
                              <Select
                                required
                                labelId={"ticketTypeSelectorLabel"}
                                id={"ticketTypeSelector"}
                                name={"ticketTypeSelector"}
                                label={"Ticket Type"}
                                value={selectedTicketType}
                                onChange={(e) => {
                                  setSelectedTicketType(e.target.value);
                                }}
                              >
                                {ticketTypes.map(({ key, name }) => {
                                  return (
                                    <MenuItem key={key} value={key}>
                                      {name}
                                    </MenuItem>
                                  );
                                })}
                              </Select>
                            </FormControl>
                            <Button
                              disabled={!selectedTicketType}
                              sx={{ width: "200px" }}
                              onClick={async (e) => {
                                e.preventDefault();
                                const ticketType = ticketTypes.find(
                                  ({ key }) => key === selectedTicketType,
                                );
                                push(
                                  orderFormFields
                                    .filter(
                                      ({
                                        collectFrom,
                                        collectForTicketTypes,
                                        ticketTypeSelection,
                                      }) => {
                                        if (
                                          collectFrom !== "both" &&
                                          collectFrom !== "attendee"
                                        ) {
                                          return false;
                                        }
                                        return (
                                          ticketTypeSelection === "all" ||
                                          collectForTicketTypes.includes(
                                            selectedTicketType,
                                          )
                                        );
                                      },
                                    )
                                    .reduce(
                                      (acc, orderFormField) => {
                                        return {
                                          ...acc,
                                          [orderFormField.key]:
                                            initialValueForOrderFormField(
                                              orderFormField,
                                            ),
                                        };
                                      },
                                      {
                                        key: uuidv4(),
                                        ticketTypeKey: selectedTicketType,
                                        price: ticketType?.usdCents || 0,
                                      },
                                    ),
                                );
                              }}
                            >
                              Add Ticket
                            </Button>
                          </Box>
                        </Box>
                      );
                    }}
                  </FieldArray>

                  <Box>
                    {purchaserOrderFormFields
                      .filter(({ contactInfo }) => !contactInfo)
                      .map((orderFormField, i) => {
                        return (
                          <Box key={`contact-field-${i}`}>
                            <OrderFormFieldView
                              {...{
                                formikProps,
                                orderFormField,
                                orderFormFieldKey: orderFormField.key,
                              }}
                            />
                          </Box>
                        );
                      })}
                  </Box>

                  <FormControlLabel
                    sx={{ marginTop: "10px" }}
                    control={
                      <Checkbox
                        checked={sendConfirmationEmail}
                        onChange={(e) => {
                          setSendConfirmationEmail(e.target.checked);
                        }}
                      />
                    }
                    label={"Send order confirmation email"}
                  />

                  <FormControlLabel
                    sx={{ marginTop: "10px" }}
                    control={
                      <Checkbox
                        checked={sendAttendees}
                        onChange={(e) => {
                          setSendAttendees(e.target.checked);
                        }}
                      />
                    }
                    label={"Send ticket emails to attendees"}
                  />

                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      gap: "20px",
                      marginTop: "20px",
                    }}
                  >
                    <Button
                      color="secondary"
                      variant="contained"
                      sx={{ flexGrow: 1 }}
                      onClick={async (e) => {
                        e.preventDefault();
                        await handleClose();
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      disabled={values?.tickets?.length === 0}
                      variant="contained"
                      type="submit"
                      sx={{ flexGrow: 1 }}
                    >
                      Save
                    </Button>
                  </Box>
                </Box>
              </Form>
            );
          }}
        </Formik>
      </Box>
    </Modal>
  );
};

export default NewTicketModal;
